import { Theme } from '@emotion/react';
import { AddCircleOutline } from '@mui/icons-material';
import { Box, Chip, Divider, SxProps, Tooltip, Typography } from '@mui/material';
import DeleteIcon from 'assets/delete-red.png';
import { ReactComponent as EditIcon } from 'assets/edit.svg';
import { ReactComponent as VerticalDots } from 'assets/vertical-dots.svg';
import { ReactComponent as ViewIcon } from 'assets/view.svg';
import { relationshipColorMap } from 'components/featured/ZoomInfo/BusinessSelected/Contacts/data';
import { Relationship } from 'components/featured/ZoomInfo/BusinessSelected/Contacts/types';
import ClientAvatar from 'components/shared/ClientAvatar';
import CustomDeleteModal from 'components/shared/CustomDeleteModal/CustomDeleteModal';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { Contact } from 'model/contacts.model';
import React, { useState } from 'react';
import { modalReducer } from 'reducers/modalReducer';
import { ContactService } from 'service/contact.service';
import zeplinColor from 'theme/Color';
import { Utilities } from 'utilities/utilities';
import styles from './ContactCard.module.css';
import { muiStyles } from './muiStyles';

const ContactCard = ({
  onViewDetails,
  contact,
  onDelete,
  onEdit,
  variant = 'default',
  onRefetch,
}: {
  onViewDetails?: (contact: Contact) => void;
  contact: Contact;
  onDelete?: (contact: any, alertText: any) => void;
  onEdit?: (contact: any) => void;
  variant?: 'default' | 'business';
  onRefetch?: () => void | Promise<void>;
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLImageElement | null>(null);
  const [modalState, modalDispatch] = React.useReducer(modalReducer, { delete: false });
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  const handleDeleteClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    modalDispatch({ type: 'OPEN', payload: 'delete' });
    setAnchorEl(null);
  };

  const relationshipKey = (contact.contactRelationship ?? 'Known') as Relationship;

  const handleAddToMyContacts = async () => {
    const payload = ContactService.buildAddToContactsPayload(contact.businessId, {
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      roles: contact.roles,
      department: contact.department,
      company: contact.company,
      profileImageUrl: contact.profileImageUrl,
      profilePicture: contact.profilePicture,
      zoominfoPersonId: contact.zoominfoPersonId,
    });
    if (!payload) {
      setAnchorEl(null);
      return;
    }
    try {
      await ContactService.addToContacts(payload);
      await onRefetch?.();
    } catch (error) {
      console.error(error);
    }
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    try {
      await ContactService.deleteContact(contact.contactId!);
      modalDispatch({ type: 'CLOSE', payload: 'delete' });
      onDelete?.('delete', 'Contact deleted successfully');
    } catch (error) {
      onDelete?.('delete', 'Failed to delete contact');
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        border: `1px solid ${zeplinColor.Neutral5}`,
        borderRadius: '14px',
        display: 'flex',
        gap: '20px',
        padding: '16px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          width: '100%',
          flexDirection: 'column',
          cursor: 'pointer',
        }}
        onClick={() => onViewDetails?.(contact)}
      >
        <Box sx={{ display: 'flex', gap: '12px', width: '100%' }}>
          <ClientAvatar
            profilePicture={contact?.profileImageUrl ?? contact?.profilePicture ?? ''}
            clientName={contact?.name}
            sx={{
              width: '50px',
              height: '50px',
            }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <Typography
                variant='responsiveHeading4'
                sx={{
                  color: zeplinColor.BasicBlack,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  maxWidth: { xs: '5rem', xl: '10rem' },
                  position: 'relative',
                }}
              >
                {contact.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                {variant === 'business' && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <Chip
                      label={
                        <Typography variant='responsiveLabel1' sx={muiStyles.chipLabel}>
                          {Utilities.formatSnakeToTitle(
                            (contact.contactRelationship ?? 'Known').replace(/\s+/g, '_'),
                          )}
                        </Typography>
                      }
                      sx={
                        [
                          relationshipColorMap[relationshipKey] ?? relationshipColorMap.Known,
                          muiStyles.chip,
                        ] as SxProps<Theme>
                      }
                    />
                  </Box>
                )}

                <LightTooltip
                  title={
                    <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                      Options
                    </Typography>
                  }
                  placement='bottom-end'
                  style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
                >
                  <VerticalDots
                    className={styles['vertical-icon']}
                    onClick={(e: any) => {
                      e.stopPropagation();
                      handleClick(e);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </LightTooltip>

                <CustomPopOver
                  onClick={(e: any) => {
                    e.stopPropagation();
                  }}
                  setAnchorEl={setAnchorEl}
                  anchorEl={anchorEl}
                  id={id}
                  sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '16px',
                      padding: '16px',
                    }}
                  >
                    {variant === 'default' ? (
                      <>
                        <Box
                          sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                          onClick={() => {
                            onViewDetails?.(contact);
                            setAnchorEl(null);
                          }}
                        >
                          {/* <img src={'/assets/view.svg'} width={18} height={18} /> */}
                          <ViewIcon width={18} height={18} />
                          <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                            View
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEdit?.(contact);
                            setAnchorEl(null);
                          }}
                        >
                          <EditIcon width={18} height={18} />
                          {/* <DeleteIcon width={18} height={18} /> */}
                          <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                            Edit
                          </Typography>
                        </Box>
                        <Box
                          sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(e);
                          }}
                        >
                          <img src={DeleteIcon} width={18} height={18} />
                          {/* <DeleteIcon width={18} height={18} /> */}
                          <Typography variant='responsiveBody3' color={zeplinColor.Error}>
                            Delete
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      <Box
                        sx={{
                          display: 'flex',
                          gap: '9px',
                          cursor: 'pointer',
                          alignItems: 'center',
                        }}
                        onClick={() => void handleAddToMyContacts()}
                      >
                        <AddCircleOutline fontSize='small' />
                        <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                          Add to My contacts
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </CustomPopOver>
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                overflow: 'hidden',
              }}
            >
              <Tooltip title={contact.roles ? contact.roles : 'No title'}>
                <Typography
                  variant='responsiveBody3'
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '160px',
                  }}
                >
                  {contact.roles ? contact.roles : 'No title'}
                </Typography>
              </Tooltip>
              <Box
                sx={{
                  height: '16px',
                  width: '1px',
                  backgroundColor: zeplinColor.Neutral5,
                  display: 'inline-block',
                  marginX: '4px',
                }}
              />
              <Typography variant='responsiveBody3'>{contact.company}</Typography>
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <img src='/assets/phone-icon.svg' alt='Contact' width={'20px'} height={'20px'} />
          <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
            {contact.phone ? contact.phone : 'N/A'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <img src='/assets/mail-dark-icon.svg' alt='Contact' width={'20px'} height={'20px'} />
          <Typography
            component='a'
            href={contact.email ? `mailto:${contact.email}` : undefined}
            onClick={(e) => e.stopPropagation()}
            variant='responsiveBody3'
            sx={{
              color: contact.email ? zeplinColor.Brand1 : zeplinColor.BasicBlack,
              cursor: contact.email ? 'pointer' : 'default',
              textDecoration: 'none',
              '&:hover': { textDecoration: contact.email ? 'underline' : 'none' },
            }}
          >
            {contact.email ? contact.email : 'N/A'}
          </Typography>
        </Box>
        {variant !== 'business' && (
          <>
            {' '}
            <Divider component='div' sx={{ borderColor: zeplinColor.Neutral5 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img
                  src='/assets/slack-user.svg'
                  alt='Contact'
                  width={'28px'}
                  height={'28px'}
                  style={{ borderRadius: '22px' }}
                />
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                  {contact?.createdByName}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <img src='/assets/clock-01.svg' alt='Contact' width={'20px'} height={'20px'} />
                <Typography variant='responsiveBody3' sx={{ color: zeplinColor.BasicBlack }}>
                  {contact?.createdAt ? Utilities.formatDate(contact.createdAt) : 'N/A'}
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
      <CustomDeleteModal
        isModalOpen={modalState.delete}
        modalDispatch={modalDispatch}
        onDelete={handleConfirmDelete}
        title='Delete Contact'
        description='Are you sure you want to delete this contact? This action cannot be undone.'
      />
    </Box>
  );
};

export default ContactCard;
